import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { format } from "date-fns";
import { enUS, arSA } from "date-fns/locale";

import MagicLinkImg from "../../../../../assets/images/payments/magicLinkCreated.png";
import QRCodeGenerator from "../../../../../shared/QRCodeGenerator/QRCodeGenerator";

import { ShareMagicLinkProps } from "../../../payment.types";

import CopyableTextField from "../Steps/CopyableTextField";
import { colors } from "../../../../../theme/palette";
import { EmailButtonIcon, QRCodeButtonIcon } from "../../../../../assets/icons/MUI/PaymentIcons";
import { useState } from "react";

const ShareMagicLink = ({ magicLink }: ShareMagicLinkProps) => {
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);
  const { t, i18n } = useTranslation();
  const { watch } = useFormContext();

  const { amount, currency, expiryDate = new Date(), paymentCategory, paymentFor, recipientsOutSideGoalngo } = watch();

  const currentLocale = i18n.language === "ar" ? arSA : enUS;
  const formattedDate = format(expiryDate, "d MMMM yyyy", { locale: currentLocale });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          rowGap: "32px",
          borderBottom: `1px solid ${colors.Gray200}`,
          marginBottom: "12px",
        }}
      >
        <Typography
          component="h3"
          sx={{
            color: "#101828",
            textAlign: "center",
            fontFamily: "Montserrat",
            fontSize: "30px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "38px",
          }}
        >
          {t("pages.payment.createPaymentLink.shareMagicLink")}
        </Typography>
        <img src={MagicLinkImg} width="173.775px" height="131px" />
        <Box>
          <Typography
            component="h6"
            sx={{
              color: "#344054",
              textAlign: "center",
              fontFamily: "Montserrat",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
            {t("pages.payment.createPaymentLink.paymentLinkCreated")}
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "var(--Gray-600, #475467)",
              textAlign: "center",
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "20px",
            }}
          >
            {t("pages.payment.createPaymentLink.paymentLinkCreatedSuccessfully")}
            <Typography component="strong" sx={{ fontWeight: 600, display: "inline-block", marginLeft: "4px" }}>
              {`${amount} ${currency}`}
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ width: "100%", padding: "0 24px 24px" }}>
          <CopyableTextField link={magicLink} />
          <Typography
            component="p"
            sx={{
              color: "var(--Gray-600, #475467)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "20px",
            }}
          >
            {t("pages.payment.createPaymentLink.paymentLinkExpire")}

            <Typography component="strong" sx={{ fontWeight: 600, display: "inline-block", margin: "0 4px" }}>
              {formattedDate}
            </Typography>

            {t("pages.payment.createPaymentLink.shareYourLink")}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          padding: "0 24px",
        }}
      >
        <Button
          variant="outlined-md"
          fullWidth
          startIcon={<EmailButtonIcon />}
          sx={{
            border: "1px solid var(--Gray-300, #D0D5DD) !important",
            color: "var(--Gray-700, #344054) !important",
            background: "transparent !important",
          }}
          onClick={() => {
            const body = magicLink;
            window.location.href = `mailto:?body="${body}"`;
          }}
        >
          {t("pages.payment.paymentLinks.email")}
        </Button>
        <Button
          variant="outlined-md"
          fullWidth
          startIcon={<QRCodeButtonIcon />}
          sx={{
            border: "1px solid var(--Gray-300, #D0D5DD) !important",
            color: "var(--Gray-700, #344054) !important",
            background: "transparent !important",
          }}
          onClick={() => setIsQRCodeVisible((prev) => !prev)}
        >
          {t("pages.payment.paymentLinks.qrCode")}
        </Button>
      </Box>
      {isQRCodeVisible && (
        <Box
          sx={{
            display: "flex",
            gap: "20px",
            padding: "24px 24px 0",
          }}
        >
          <QRCodeGenerator value={magicLink} />
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "10px", flex: 1 }}>
            <Typography
              component="h5"
              sx={{
                color: "var(--Gray-700, #344054)",
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "28px",
              }}
            >
              {t("pages.payment.paymentLinks.shareQRCode")}
            </Typography>
            <CopyableTextField link={magicLink} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ShareMagicLink;
